<template>
    <div class="p-4">
        <!-- Search input (visible on all sizes) -->
        <div class="mb-4">
            <div class="input-group">
                <span class="input-group-text bg-white">
                    <i class="bi bi-search text-muted" />
                </span>
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="form-control"
                    placeholder="Buscar avisos y partes..."
                    @input="onSearchInput"
                    @keydown.enter="aplicarBusqueda"
                />
                <button v-if="searchQuery" class="btn btn-outline-secondary" type="button" @click="limpiarBusqueda">
                    <i class="bi bi-x" />
                </button>
            </div>
        </div>

        <!-- Sin query -->
        <div v-if="!q" class="text-center text-muted py-5">
            <i class="bi bi-search fs-2 d-block mb-2" />
            Escribe algo para buscar
        </div>

        <!-- Loading -->
        <div v-else-if="loading" class="text-center py-5">
            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                <span class="visually-hidden">Buscando...</span>
            </div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="alert alert-danger">
            {{ error }}
        </div>

        <!-- Sin resultados -->
        <div v-else-if="avisos.length === 0 && partes.length === 0" class="text-center text-muted py-5">
            No se han encontrado resultados para «{{ q }}»
        </div>

        <!-- Resultados -->
        <div v-else class="row g-4">
            <!-- Columna Avisos -->
            <div class="col-12 col-md-6">
                <h6 class="text-muted text-uppercase small fw-semibold mb-2">
                    <i class="bi bi-bell me-1" />
                    Avisos
                    <span class="text-secondary fw-normal">({{ avisos.length }})</span>
                </h6>
                <div class="card">
                    <div v-if="avisos.length === 0" class="card-body text-muted small text-center py-3">
                        Sin avisos
                    </div>
                    <template v-else>
                        <div class="list-group list-group-flush overflow-hidden">
                            <RouterLink
                                v-for="aviso in avisos"
                                :key="aviso.idAviso"
                                class="list-group-item list-group-item-action text-decoration-none text-dark"
                                :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }"
                                :title="aviso.descripcion"
                            >
                                <div class="d-flex justify-content-between align-items-start">
                                    <span class="text-muted small">#{{ aviso.idAviso }}</span>
                                    <span
                                        class="badge ms-2"
                                        :class="aviso.finalizado ? 'bg-success' : 'bg-warning text-dark'"
                                    >
                                        {{ aviso.finalizado ? 'Cerrado' : 'Abierto' }}
                                    </span>
                                </div>
                                <div class="small fw-semibold text-clamp-2 mt-1" v-html="highlight(aviso.descripcion, q)" />
                                <div v-if="aviso.cliente" class="small text-muted mt-1" v-html="highlight(aviso.cliente.nombreFiscal, q)" />
                            </RouterLink>
                        </div>
                        <div v-if="avisosTotalCount > avisos.length" class="card-footer text-center py-2">
                            <RouterLink :to="{ name: 'mis-avisos', query: { search: q, todos: '1', finalizado: 'todos' } }" class="small">
                                Ver los {{ avisosTotalCount }} avisos
                            </RouterLink>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Columna Partes -->
            <div class="col-12 col-md-6">
                <h6 class="text-muted text-uppercase small fw-semibold mb-2">
                    <i class="bi bi-file-text me-1" />
                    Partes
                    <span class="text-secondary fw-normal">({{ partes.length }})</span>
                </h6>
                <div class="card">
                    <div v-if="partes.length === 0" class="card-body text-muted small text-center py-3">
                        Sin partes
                    </div>
                    <template v-else>
                        <div class="list-group list-group-flush overflow-hidden">
                            <RouterLink
                                v-for="parte in partes"
                                :key="parte.idParte"
                                class="list-group-item list-group-item-action text-decoration-none text-dark"
                                :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }"
                                :title="parte.observaciones || parte.tipoParte?.descripcion || parte.cliente?.nombreFiscal || ''"
                            >
                                <div class="d-flex justify-content-between align-items-start">
                                    <span class="text-muted small">#{{ parte.idParte }} · {{ formatFecha(parte.fecha) }}</span>
                                    <span
                                        class="badge ms-2"
                                        :class="parte.cerrado ? 'bg-success' : 'bg-warning text-dark'"
                                    >
                                        {{ parte.cerrado ? 'Cerrado' : 'Abierto' }}
                                    </span>
                                </div>
                                <div v-if="parte.tipoParte" class="small fw-semibold mt-1">{{ parte.tipoParte.descripcion }}</div>
                                <div v-if="parte.cliente" class="small text-muted mt-1" v-html="highlight(parte.cliente.nombreFiscal, q)" />
                                <div v-if="parte.observaciones" class="small text-muted text-clamp-2" v-html="highlight(parte.observaciones, q)" />
                            </RouterLink>
                        </div>
                        <div v-if="partesTotalCount > partes.length" class="card-footer text-center py-2">
                            <RouterLink :to="{ name: 'mis-partes', query: { search: q, todos: '1' } }" class="small">
                                Ver los {{ partesTotalCount }} partes
                            </RouterLink>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import { avisosApi } from '@/api/avisosApi'
import { partesApi } from '@/api/partesApi'
import type { Aviso } from '@/types/aviso'
import type { Parte } from '@/types/parte'
import { highlight } from '@/utils/highlight'

usePageHeader('Búsqueda')
useBreadcrumbs([{ label: 'Búsqueda' }])

const route = useRoute()
const router = useRouter()

const q = computed(() => (route.query.q as string) ?? '')
const loading = ref(false)
const error = ref<string | null>(null)
const avisos = ref<Aviso[]>([])
const partes = ref<Parte[]>([])
const avisosTotalCount = ref(0)
const partesTotalCount = ref(0)

// Search input
const searchInput = ref<HTMLInputElement | null>(null)
const searchQuery = ref(q.value)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

// Sincronizar input con el query param
watch(q, (val) => {
    searchQuery.value = val
})

// Autofocus cuando no hay query
watch(searchInput, (el) => {
    if (el && !q.value) nextTick(() => el.focus())
}, { immediate: true })

function onSearchInput() {
    if (debounceTimer) clearTimeout(debounceTimer)
    const val = searchQuery.value.trim()
    if (val.length < 2) return
    debounceTimer = setTimeout(() => aplicarBusqueda(), 350)
}

function aplicarBusqueda() {
    const val = searchQuery.value.trim()
    if (!val) return
    router.push({ name: 'search', query: { q: val } })
}

function limpiarBusqueda() {
    searchQuery.value = ''
    router.push({ name: 'search' })
    nextTick(() => searchInput.value?.focus())
}

function formatFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

async function buscar(query: string) {
    if (!query.trim()) {
        avisos.value = []
        partes.value = []
        return
    }
    loading.value = true
    error.value = null
    try {
        const [avisosRes, partesRes] = await Promise.all([
            avisosApi.getAll({ search: query, pageSize: 5 }),
            partesApi.getAll({ search: query, pageSize: 5 }),
        ])
        avisos.value = avisosRes.items
        avisosTotalCount.value = avisosRes.totalCount
        partes.value = partesRes.items
        partesTotalCount.value = partesRes.totalCount
    } catch {
        error.value = 'No se pudieron cargar los resultados.'
    } finally {
        loading.value = false
    }
}

watch(q, (val) => buscar(val), { immediate: true })
</script>

<style scoped>
.text-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
